/**
 * MenuItem - Row item for dropdown menus and selection lists
 * 
 * @param {ReactNode} icon - Optional icon element (left side)
 * @param {'default'|'media'} iconVariant - Optional leading icon layout
 * @param {ReactNode} children - Label text (required)
 * @param {function} onClick - Click handler
 * @param {function} onMouseDown - MouseDown handler (for stopPropagation in HUD contexts)
 * @param {function} onMouseEnter - MouseEnter handler (for hover-preview surfaces)
 * @param {function} onFocus - Focus handler (keyboard parity for hover-preview surfaces)
 * @param {boolean} selected - Highlight for current selection
 * @param {boolean} active - Highlight for active/current menu state
 * @param {boolean} accent - Accent-colored text (for special items like "New Session")
 * @param {boolean} disabled
 * @param {'compact'|'default'} size - 'compact' uses caption text, 'default' uses body text
 * @param {ReactNode} secondaryText - Optional right-aligned secondary content
 * @param {'default'|'checkmark'} selectionVariant - Optional selected state treatment
 * @param {'trailing'|'leading'} checkmarkPlacement - Which end of the row the checkmark sits
 *   at. Only read when selectionVariant is 'checkmark'. 'leading' puts it in the icon column
 *   instead, overlaid on the caller's own `icon` so the column keeps the width the caller's
 *   other rows use — for lists where a trailing checkmark would sit far from the icons every
 *   sibling row aligns to.
 * @param {ReactNode} trailingAction - Optional secondary control rendered at the row's end
 *   (e.g. a settings gear). Its presence changes the ROOT ELEMENT from a button to a row,
 *   because a button inside a button is invalid HTML and does not reliably receive clicks.
 * @param {function} onTrailingAction - Click handler for the trailing control
 * @param {string} trailingActionLabel - Accessible label for the trailing control (required
 *   when trailingAction is set; it is an icon-only button)
 * @param {'always'|'hover'} trailingActionReveal - Whether the trailing control is visible at
 *   rest or only while the row is hovered or holds focus. 'hover' fades it with OPACITY and
 *   never with display, so the cell keeps its width and the label cannot shift under the
 *   pointer; focus-within counts too, or the control becomes unreachable by keyboard.
 * @param {boolean} trailingActionDismissPopover - Whether the TRAILING control closes a
 *   parent PopoverButton. Defaults to dismissPopover, i.e. either half closes the menu.
 *   Pass false for a trailing control that acts in place — one that expands a section
 *   rather than committing a choice, where closing the menu would undo the thing it did.
 * @param {boolean} trailingActionExpanded - Forwarded as aria-expanded on the trailing
 *   control, for a trailing action that discloses something. Omitted when undefined.
 * @param {boolean} dismissPopover - Whether selecting this item should close a parent PopoverButton
 * @param {string} ariaLabel - Accessible label override
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles. Background and text colour are NOT among
 *   them: CSS owns the row's plate and ink (see `.ui-menu-item` in primitives.css), keyed on
 *   the data attributes below. Setting either here is a deliberate caller override that beats
 *   the row's own states, which is why destructive rows can stay red at rest.
 */
const CheckmarkIcon = () => (
  <svg className="ui-menu-item__checkmark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const MenuItem = ({
  icon,
  iconVariant = 'default',
  children,
  onClick,
  onMouseDown,
  onMouseEnter,
  onFocus,
  selected = false,
  active = false,
  accent = false,
  disabled = false,
  size = 'compact',
  secondaryText,
  selectionVariant = 'default',
  checkmarkPlacement = 'trailing',
  trailingAction = null,
  onTrailingAction,
  trailingActionLabel,
  trailingActionReveal = 'always',
  trailingActionDismissPopover,
  trailingActionExpanded,
  dismissPopover = true,
  ariaLabel,
  className = '',
  style = {}
}) => {
  const isDefault = size === 'default';
  const isCheckmarkSelection = selectionVariant === 'checkmark';
  const isMediaIcon = iconVariant === 'media';
  const isChosen = selected || active;
  const isLeadingCheckmark = isCheckmarkSelection && checkmarkPlacement === 'leading';
  const resolvedSecondaryText = isCheckmarkSelection && isChosen && !isLeadingCheckmark
    ? <CheckmarkIcon />
    : secondaryText;

  const baseStyle = {
    width: '100%',
    padding: isDefault
      ? 'var(--dropdown-item-padding)'
      : 'var(--ui-menu-item-padding-block) var(--ui-menu-item-padding-inline)',
    paddingInline: isMediaIcon ? 'var(--ui-menu-item-media-inset-inline)' : undefined,
    border: 'none',
    fontSize: isDefault ? 'var(--dropdown-item-font)' : 'var(--ui-menu-item-font-size)',
    fontWeight: accent ? 600 : 400,
    textAlign: 'left',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: isDefault ? 'var(--dropdown-item-gap)' : 'var(--ui-menu-item-gap)',
    borderRadius: isDefault ? 'var(--dropdown-item-radius)' : 'var(--ui-menu-item-radius)',
    outline: 'none',
    ...style
  };

  const rowContent = (
    <>
      {isLeadingCheckmark ? (
        <span className="ui-menu-item__leading-check">
          {icon}
          {isChosen ? <CheckmarkIcon /> : null}
        </span>
      ) : isMediaIcon ? (
        <span className="ui-menu-item__icon-slot ui-media-thumbnail-backdrop">
          {icon}
        </span>
      ) : icon}
      <span style={{
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        // The slack rides the label slot, not the row's end padding. On a stretched row the
        // flex:1 label absorbs it and nothing moves, so trailing checkmarks and secondary
        // text stay put across every menu in the app; on a natural-width (max-content)
        // surface it joins the row's intrinsic width and the panel grows. Blast radius lands
        // on exactly the case that was flush.
        paddingInlineEnd: 'var(--ui-menu-item-trailing-slack)',
      }}>
        {children}
      </span>
      {resolvedSecondaryText && (
        <span style={{ color: active && !isCheckmarkSelection ? 'var(--text-secondary)' : 'var(--text-muted)', fontSize: isDefault ? 'var(--text-caption)' : 'var(--ui-menu-item-font-size)', flexShrink: 0, whiteSpace: 'nowrap' }}>
          {resolvedSecondaryText}
        </span>
      )}
    </>
  );

  const sharedButtonProps = {
    type: 'button',
    onClick,
    onMouseDown,
    onMouseEnter,
    onFocus,
    disabled,
    'aria-label': ariaLabel,
    'aria-pressed': isCheckmarkSelection ? isChosen : undefined,
    'data-selected': !isCheckmarkSelection && isChosen ? 'true' : undefined,
    'data-active': !isCheckmarkSelection && active ? 'true' : undefined,
    'data-selection-variant': isCheckmarkSelection ? 'checkmark' : undefined,
    'data-checked': isCheckmarkSelection && isChosen ? 'true' : undefined,
    'data-icon-variant': isMediaIcon ? 'media' : undefined,
    'data-accent': accent,
    'data-popover-dismiss': dismissPopover ? 'true' : undefined,
  };

  if (!trailingAction) {
    return (
      <button {...sharedButtonProps} className={`ui-menu-item ${className}`} style={baseStyle}>
        {rowContent}
      </button>
    );
  }

  /**
   * ⛔ THE ROOT IS NO LONGER A BUTTON, AND IT CANNOT BE.
   *
   * A trailing control has to be its own button — it runs a different action, needs its own
   * accessible name, and must be reachable by keyboard on its own. Nesting one inside this
   * row's button is invalid HTML, and browsers do not deliver its clicks reliably. So when a
   * trailing action is present the row becomes a plain container holding TWO sibling buttons.
   *
   * Consequences that are load-bearing, not incidental:
   * - `.ui-menu-item` styling moves to the PRIMARY button so hover, selected and active
   *   states keep working unchanged for every existing consumer.
   * - `data-popover-dismiss` is carried by BOTH buttons by default. The dropdown closes on
   *   either action; a gear that left the menu open would strand it over the composer it
   *   just opened. Dismissal is per half, though: a trailing control that acts IN PLACE —
   *   one that discloses a section rather than committing a choice — passes
   *   `trailingActionDismissPopover={false}` and carries no attribute, so the menu it just
   *   changed stays open. That is settled here, on the attribute, rather than by a
   *   `stopPropagation` or an edit to `PopoverButton`, which owns the dismissal contract.
   * - A CHOSEN row plates both halves (`data-active`/`data-selected` on the container, styled
   *   in primitives.css). Without that the selection stops at the primary button's edge and
   *   the trailing cell reads as a gap punched in the row's own highlight.
   * - The two buttons are SIBLINGS, so the gear's click cannot reach the row's action by
   *   bubbling at all. That is structural, not guarded: a stopPropagation here would be
   *   inert, and a kill matrix confirmed no test could ever exercise it. Consumers still
   *   stop propagation for their OWN ancestors, which is a different question.
   */
  return (
    <div
      className={`ui-menu-item-row ${className}`}
      data-trailing-reveal={trailingActionReveal === 'hover' ? 'hover' : undefined}
      data-selected={sharedButtonProps['data-selected']}
      data-active={sharedButtonProps['data-active']}
      style={{ display: 'flex', alignItems: 'stretch', width: '100%' }}
    >
      <button
        {...sharedButtonProps}
        className="ui-menu-item ui-menu-item--has-trailing"
        style={baseStyle}
      >
        {rowContent}
      </button>
      <button
        type="button"
        className="ui-menu-item__trailing-action"
        onClick={(event) => onTrailingAction?.(event)}
        onMouseDown={(event) => event.stopPropagation()}
        disabled={disabled}
        aria-label={trailingActionLabel}
        aria-expanded={trailingActionExpanded}
        title={trailingActionLabel}
        data-popover-dismiss={
          (trailingActionDismissPopover ?? dismissPopover) ? 'true' : undefined
        }
      >
        {trailingAction}
      </button>
    </div>
  );
};

export default MenuItem;
